<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        header="ผูกหน่วยงานกับถังดับเพลิง (ถังเปล่า)"
        :modal="true"
        :closable="true"
        :style="{ width: '700px' }"
        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    >
        <div class="assign-organization-content">
            <div class="p-fluid">
                <!-- ช่วงรหัสถัง -->
                <div class="p-field">
                    <label>ช่วงรหัสถัง <span style="color: red">*</span></label>
                    <div class="p-inputgroup">
                        <InputText v-model="startCode" placeholder="รหัสเริ่มต้น เช่น D25032101" :class="isValidStartCode" />
                        <span class="p-inputgroup-addon">ถึง</span>
                        <InputText v-model="endCode" placeholder="รหัสสิ้นสุด เช่น D25033900" :class="isValidEndCode" />
                    </div>
                    <small v-if="submitted && (!startCode || !endCode)" class="p-error"> กรุณาระบุช่วงรหัสถัง </small>
                    <small v-if="codeRangeError" class="p-error">
                        {{ codeRangeError }}
                    </small>
                </div>

                <!-- เลือกหน่วยงาน -->
                <div class="p-field">
                    <label for="organization">หน่วยงาน <span style="color: red">*</span></label>
                    <Dropdown
                        id="organization"
                        v-model="selectedOrganization"
                        :options="organizationList"
                        optionLabel="organize_name"
                        optionValue="organize_id"
                        placeholder="เลือกหน่วยงาน"
                        :class="isValidOrganization"
                        :loading="loadingOrganizations"
                        filter
                        showClear
                    />
                    <small v-if="submitted && !selectedOrganization" class="p-error"> กรุณาเลือกหน่วยงาน </small>
                </div>

                <!-- รายละเอียดเพิ่มเติม -->
                <div class="p-field">
                    <label for="description">รายละเอียดเพิ่มเติม</label>
                    <Textarea id="description" v-model="description" rows="3" placeholder="รายละเอียดการผูกหน่วยงาน (ถ้ามี)" />
                </div>

                <!-- ข้อมูลช่วงที่เลือก -->
                <div class="p-field" v-if="rangeInfo">
                    <label>ข้อมูลช่วงที่เลือก:</label>
                    <div class="range-info">
                        <div class="info-item"><strong>ช่วงรหัส:</strong> {{ startCode }} - {{ endCode }}</div>
                        <div class="info-item"><strong>จำนวนถัง:</strong> {{ rangeInfo.totalCount }} ถัง</div>
                        <div class="info-item" v-if="rangeInfo.availableCount !== rangeInfo.totalCount">
                            <strong>ถังที่ว่าง:</strong> {{ rangeInfo.availableCount }} ถัง
                            <small class="p-text-secondary"> ({{ rangeInfo.totalCount - rangeInfo.availableCount }} ถังถูกผูกแล้ว)</small>
                        </div>
                        <div class="info-item" v-if="rangeInfo.organizationName">
                            <strong>หน่วยงานที่เลือก:</strong> {{ rangeInfo.organizationName }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="p-d-flex p-jc-between">
                <Button
                    label="ตรวจสอบช่วง"
                    icon="pi pi-search"
                    class="p-button-info"
                    :loading="checkingRange"
                    @click="checkRange"
                    :disabled="!startCode || !endCode"
                />
                <div>
                    <Button label="ยกเลิก" icon="pi pi-times" class="p-button-text p-mr-2" @click="closeModal" />
                    <Button label="บันทึก" icon="pi pi-check" :loading="loading" @click="assignOrganization" />
                </div>
            </div>
        </template>

        <!-- Result Dialog -->
        <Dialog v-model:visible="showResult" header="ผลการผูกหน่วยงาน" :modal="true" :closable="false" :style="{ width: '500px' }">
            <div class="result-content">
                <div class="p-d-flex p-ai-center p-mb-3">
                    <i class="pi pi-check-circle p-mr-2" style="color: #4caf50; font-size: 2rem"></i>
                    <div>
                        <h4 class="p-m-0">ผูกหน่วยงานสำเร็จ!</h4>
                        <p class="p-m-0">ผูกถังดับเพลิงจำนวน {{ assignedCount }} ถัง</p>
                    </div>
                </div>

                <div class="assignment-details">
                    <h5>รายละเอียด:</h5>
                    <div class="detail-item"><strong>ช่วงรหัส:</strong> {{ resultData.startCode }} - {{ resultData.endCode }}</div>
                    <div class="detail-item"><strong>หน่วยงาน:</strong> {{ resultData.organizationName }}</div>
                    <div class="detail-item"><strong>จำนวนที่ผูก:</strong> {{ assignedCount }} ถัง</div>
                </div>
            </div>

            <template #footer>
                <Button label="ตกลง" icon="pi pi-check" @click="closeResult" />
            </template>
        </Dialog>
    </Dialog>
</template>

<script>
export default {
    name: 'AssignOrganizationModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:visible', 'assigned'],
    data() {
        return {
            startCode: '',
            endCode: '',
            selectedOrganization: null,
            description: '',
            submitted: false,
            loading: false,
            loadingOrganizations: false,
            checkingRange: false,
            showResult: false,
            assignedCount: 0,
            resultData: {},
            rangeInfo: null,
            codeRangeError: '',
            organizationList: []
        };
    },

    watch: {
        visible(newVal) {
            if (newVal) {
                this.resetForm();
                this.loadOrganizations();
            }
        },
        selectedOrganization() {
            this.updateRangeInfo();
        }
    },

    computed: {
        isValidStartCode() {
            return { 'p-invalid': this.submitted && !this.startCode };
        },
        isValidEndCode() {
            return { 'p-invalid': this.submitted && !this.endCode };
        },
        isValidOrganization() {
            return { 'p-invalid': this.submitted && !this.selectedOrganization };
        }
    },

    methods: {
        // โหลดรายการหน่วยงาน
        async loadOrganizations() {
            this.loadingOrganizations = true;
            try {
                const response = await this.axios.post(`${this.$url_service}/initial/organize`, {
                    body: {
                        topic: 'search-table',
                        name: '',
                        type: null,
                        province: null,
                        amphur: null,
                        tambon: null,
                        organize: [],
                        userOrg: null
                    }
                });

                if (response.status == 200) {
                    this.organizationList = response.data.data;
                } else {
                    throw new Error(response.data.message || 'ไม่สามารถโหลดรายการหน่วยงานได้');
                }
            } catch (error) {
                console.error('Error loading organizations:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'เกิดข้อผิดพลาด',
                    detail: 'ไม่สามารถโหลดรายการหน่วยงานได้',
                    life: 3000
                });
            } finally {
                this.loadingOrganizations = false;
            }
        },

        // ตรวจสอบช่วงรหัส
        async checkRange() {
            if (!this.startCode || !this.endCode) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'ข้อมูลไม่ครบ',
                    detail: 'กรุณาระบุช่วงรหัสถัง',
                    life: 3000
                });
                return;
            }

            // ตรวจสอบ format รหัส
            if (this.startCode.length < 9) {
                this.codeRangeError = 'รหัสถังต้องมี 9 - 10 หลัก เช่น D25032101';
                return;
            }

            // ตรวจสอบว่า startCode <= endCode
            if (this.startCode > this.endCode) {
                this.codeRangeError = 'รหัสเริ่มต้นต้องน้อยกว่าหรือเท่ากับรหัสสิ้นสุด';
                return;
            }

            this.codeRangeError = '';
            this.checkingRange = true;

            try {
                const response = await this.axios.post(`${this.$url_service}/initial/extinguisher`, {
                    body: {
                        topic: 'check-extinguisher-range',
                        startCode: this.startCode,
                        endCode: this.endCode
                    }
                });

                console.log('Check range response:', response.data);

                if (response.data.success) {
                    this.rangeInfo = response.data.data;
                    this.updateRangeInfo();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'ตรวจสอบช่วงสำเร็จ',
                        detail: `พบถังในช่วงนี้ ${this.rangeInfo.totalCount} ถัง (ว่าง ${this.rangeInfo.availableCount} ถัง)`,
                        life: 3000
                    });
                } else {
                    this.codeRangeError = response.data.message || 'ไม่พบถังในช่วงที่ระบุ';
                    this.rangeInfo = null;

                    this.$toast.add({
                        severity: 'warn',
                        summary: 'ไม่พบข้อมูล',
                        detail: this.codeRangeError,
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error checking range:', error);
                this.codeRangeError = 'เกิดข้อผิดพลาดในการตรวจสอบช่วง';
                this.rangeInfo = null;

                this.$toast.add({
                    severity: 'error',
                    summary: 'เกิดข้อผิดพลาด',
                    detail: 'ไม่สามารถตรวจสอบช่วงรหัสได้',
                    life: 3000
                });
            } finally {
                this.checkingRange = false;
            }
        },

        // อัพเดทข้อมูลช่วง
        updateRangeInfo() {
            if (this.rangeInfo && this.selectedOrganization) {
                const org = this.organizationList.find((o) => o.organize_id === this.selectedOrganization);
                this.rangeInfo.organizationName = org ? org.organize_name : '';
            }
        },

        getUser() {
            const getUser = this.decodeJWT(localStorage.getItem('token'));
            if (typeof getUser == 'string') {
                return JSON.parse(getUser);
            }
            return getUser;
        },

        // ผูกหน่วยงาน
        async assignOrganization() {
            this.submitted = true;

            // Validation
            if (!this.startCode || !this.endCode || !this.selectedOrganization) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'ข้อมูลไม่ครบถ้วน',
                    detail: 'กรุณากรอกข้อมูลให้ครบถ้วน',
                    life: 3000
                });
                return;
            }

            if (this.codeRangeError) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'ข้อมูลไม่ถูกต้อง',
                    detail: this.codeRangeError,
                    life: 3000
                });
                return;
            }

            if (!this.rangeInfo) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'กรุณาตรวจสอบช่วง',
                    detail: 'กรุณากดปุ่ม "ตรวจสอบช่วง" ก่อนบันทึก',
                    life: 3000
                });
                return;
            }

            if (this.rangeInfo.availableCount === 0) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'ไม่มีถังว่าง',
                    detail: 'ไม่มีถังเปล่าในช่วงนี้ที่สามารถผูกหน่วยงานได้',
                    life: 3000
                });
                return;
            }

            this.loading = true;

            try {
                const org = this.organizationList.find((o) => o.organize_id === this.selectedOrganization);

                const payload = {
                    topic: 'assign-organization-to-extinguishers',
                    startCode: this.startCode,
                    endCode: this.endCode,
                    organizationId: this.selectedOrganization,
                    organizationName: org.organize_name,
                    description: this.description,
                    userData: this.getUser()
                };

                console.log('Assign payload:', payload);

                const response = await this.axios.post(`${this.$url_service}/initial/extinguisher`, {
                    body: payload
                });

                console.log('Assign response:', response.data);

                if (response.data.success) {
                    this.assignedCount = response.data.assignedCount;
                    this.resultData = {
                        startCode: this.startCode,
                        endCode: this.endCode,
                        organizationName: org.organize_name
                    };
                    this.showResult = true;

                    // Emit event เพื่อให้ parent component รู้ว่ามีการผูกหน่วยงาน
                    this.$emit('assigned', {
                        count: this.assignedCount,
                        startCode: this.startCode,
                        endCode: this.endCode,
                        organizationName: org.organize_name,
                        organizationId: this.selectedOrganization
                    });

                    this.$fire({
                        title: 'สำเร็จ',
                        type: 'success',
                        timer: 3000,
                        detail: `ผูกหน่วยงานกับถังดับเพลิงจำนวน ${this.assignedCount} ถัง`,
                        confirmButtonColor: '#28B463'
                    });
                } else {
                    throw new Error(response.data.message || 'เกิดข้อผิดพลาด');
                }
            } catch (error) {
                console.error('Error assigning organization:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'เกิดข้อผิดพลาด',
                    detail: error.response?.data?.message || error.message || 'ไม่สามารถผูกหน่วยงานได้',
                    life: 3000
                });
            } finally {
                this.loading = false;
                this.$emit('update:visible', false);
            }
        },

        // รีเซ็ตฟอร์ม
        resetForm() {
            this.startCode = '';
            this.endCode = '';
            this.selectedOrganization = null;
            this.description = '';
            this.submitted = false;
            this.rangeInfo = null;
            this.codeRangeError = '';
        },

        // ปิด modal
        closeModal() {
            this.resetForm();
            this.$emit('update:visible', false);
        },

        // ปิด result dialog
        closeResult() {
            this.showResult = false;
            this.assignedCount = 0;
            this.resultData = {};
            this.closeModal();
        }
    }
};
</script>

<style scoped>
.assign-organization-content {
    padding: 0;
}

.range-info {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1rem;
    margin-top: 0.5rem;
}

.info-item {
    margin-bottom: 0.5rem;
    padding: 0.25rem 0;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-item strong {
    color: #495057;
}

.result-content .assignment-details {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-top: 1rem;
}

.detail-item {
    margin-bottom: 0.5rem;
    padding: 0.25rem 0;
}

.detail-item:last-child {
    margin-bottom: 0;
}

.detail-item strong {
    color: #495057;
}

/* Loading state */
.p-button:disabled {
    opacity: 0.6;
}

/* Error styling */
.p-error {
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

/* Success styling for range info */
.range-info {
    border-left: 4px solid #28a745;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .p-d-flex.p-jc-between {
        flex-direction: column;
        gap: 1rem;
    }

    .p-d-flex.p-jc-between > div:last-child {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
    }
}
</style>
